import { cva, type VariantProps } from 'class-variance-authority';
import { css, injectGlobal } from '@emotion/css';
import type {
  StylesConfig,
  StyleColors,
  HeadingLevel,
  StyleTypography,
  HeadingTypography,
  BodyTypography,
  BodySize,
  HeadingTag,
  LinkVariant,
} from '../types/styles';
import {
  getGlobalStyleColor,
  getGlobalTextColorStyle,
  getTextColorStyleWithDisplay,
} from '../utils/styleHelpers';
import {
  MOBILE_CONTAINER_QUERY_HEADER,
  MOBILE_MEDIA_QUERY_HEADER,
  TABLET_CONTAINER_QUERY_HEADER,
  TABLET_MEDIA_QUERY_HEADER,
} from '../utils/constants';
import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import { FontManager } from '../fonts/manager';

const headingCss = (
  styles: StyleTypography,
  colors: StyleColors,
  tag: HeadingTag,
  isServerRendering: boolean,
): string => {
  const heading = styles.heading;
  const level: HeadingLevel = heading[tag];

  const textColorStyle = getGlobalTextColorStyle(level.fontColor, colors);

  const finalStyle: CSSInterpolation = {
    fontFamily: heading.fontFamily,
    fontWeight: level.fontWeight,
    letterSpacing: level.letterSpacing,
    lineHeight: level.lineHeight,
    fontSize: level.desktop.fontSize + styles.fontSizeUnit,
    // Only apply color to container if it's not a gradient (gradients need webkit properties on text leaves)
    ...(textColorStyle.color && !textColorStyle.background && { color: textColorStyle.color }),
  };

  if (level.backgroundColor) {
    finalStyle.background = getGlobalStyleColor(level.backgroundColor, colors);
  }
  if (isServerRendering) {
    finalStyle[MOBILE_MEDIA_QUERY_HEADER] = {
      fontSize: level.mobile.fontSize + styles.fontSizeUnit,
    };
    finalStyle[TABLET_MEDIA_QUERY_HEADER] = {
      fontSize: level.tablet.fontSize + styles.fontSizeUnit,
    };
  } else {
    finalStyle[MOBILE_CONTAINER_QUERY_HEADER] = {
      fontSize: level.mobile.fontSize + styles.fontSizeUnit,
    };
    finalStyle[TABLET_CONTAINER_QUERY_HEADER] = {
      fontSize: level.tablet.fontSize + styles.fontSizeUnit,
    };
  }
  return css(finalStyle);
};

const bodyCss = (styles: StyleTypography, colors: StyleColors, size: BodySize): string => {
  const body = styles.body;
  const sizeLevel = body[size];

  const textColorStyle = getGlobalTextColorStyle(sizeLevel.fontColor, colors);

  const finalStyle: CSSInterpolation = {
    fontFamily: body.fontFamily,
    fontWeight: sizeLevel.fontWeight,
    letterSpacing: sizeLevel.letterSpacing + 'em',
    lineHeight: sizeLevel.lineHeight,
    fontSize: sizeLevel.fontSize + styles.fontSizeUnit,
    // Only apply color to container if it's not a gradient (gradients need webkit properties on text leaves)
    ...(textColorStyle.color && !textColorStyle.background && { color: textColorStyle.color }),
  };

  if (sizeLevel.backgroundColor) {
    finalStyle.background = getGlobalStyleColor(sizeLevel.backgroundColor, colors);
  }

  return css(finalStyle);
};

const linkCss = (styles: StyleTypography, colors: StyleColors, variant: LinkVariant): string => {
  const link = styles.link[variant];
  const decoration = link.decoration?.trim() || 'none';

  // Get text color style (gradients will use background property with background-clip: text)
  const textColorStyle = getGlobalTextColorStyle(link.fontColor, colors);
  const bgColorString = link.backgroundColor
    ? getGlobalStyleColor(link.backgroundColor, colors)
    : undefined;

  // Prepare text color styles for span (like buttons do)
  const textColorStyleWithDisplay = textColorStyle.background
    ? getTextColorStyleWithDisplay(textColorStyle)
    : textColorStyle;
  const spanStyles = css(textColorStyleWithDisplay as CSSObject);

  const finalStyle: CSSInterpolation = {
    fontWeight: link.fontWeight,
    ...(bgColorString && { background: bgColorString }),
    textDecoration: decoration,
    // Apply text decoration color for solid colors only (gradients don't support underline)
    ...(textColorStyle.color && decoration !== 'none'
      ? { textDecorationColor: textColorStyle.color }
      : {}),
    // Apply text color styles to child span (like buttons do)
    '& > span': spanStyles,
  };

  return css(finalStyle);
};

const injectAllFontVariants = (stylesConfig: StylesConfig, fontManager: FontManager) => {
  const typography: StyleTypography = stylesConfig.typography;
  const heading: HeadingTypography = typography.heading;
  // Global styles no longer control italics, but element-level italics should
  // still render with real italic faces when available.
  ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].forEach((key) => {
    const headingSubVariant = heading[key as HeadingTag];
    const fontFaceCss = fontManager.getFontFaceCss(
      heading.fontFamily,
      headingSubVariant.fontWeight,
      'normal',
    );
    if (fontFaceCss) {
      injectGlobal(fontFaceCss);
    }
    const italicFontFaceCss = fontManager.getFontFaceCss(
      heading.fontFamily,
      headingSubVariant.fontWeight,
      'italic',
    );
    if (italicFontFaceCss) {
      injectGlobal(italicFontFaceCss);
    }
  });

  const body: BodyTypography = typography.body;
  (['small', 'medium', 'large'] as const).forEach((size) => {
    const sizeLevel = body[size];
    const fontFaceCss = fontManager.getFontFaceCss(body.fontFamily, sizeLevel.fontWeight, 'normal');
    if (fontFaceCss) {
      injectGlobal(fontFaceCss);
    }
    const italicFontFaceCss = fontManager.getFontFaceCss(
      body.fontFamily,
      sizeLevel.fontWeight,
      'italic',
    );
    if (italicFontFaceCss) {
      injectGlobal(italicFontFaceCss);
    }
  });
};

// Single source of truth for typography style variant keys.
// Keep in sync with the variant object inside createTypographyVariants below and any
// validator that references these values (e.g. TextPropsDto.ts STYLE_VARIANT_VALUES).
export const TYPOGRAPHY_STYLE_VARIANT_VALUES = [
  'heading.h1',
  'heading.h2',
  'heading.h3',
  'heading.h4',
  'heading.h5',
  'heading.h6',
  'body.small',
  'body.medium',
  'body.large',
  'link.link1',
  'link.link2',
] as const;

export type TypographyStyleVariant = (typeof TYPOGRAPHY_STYLE_VARIANT_VALUES)[number];

export const createTypographyVariants = (
  stylesConfig: StylesConfig,
  fontManager: FontManager,
  isServerRendering: boolean,
) => {
  const typography = stylesConfig.typography;
  const colors = stylesConfig.colors;
  injectAllFontVariants(stylesConfig, fontManager);

  return cva('', {
    variants: {
      variant: {
        'heading.h1': headingCss(typography, colors, 'h1', isServerRendering),
        'heading.h2': headingCss(typography, colors, 'h2', isServerRendering),
        'heading.h3': headingCss(typography, colors, 'h3', isServerRendering),
        'heading.h4': headingCss(typography, colors, 'h4', isServerRendering),
        'heading.h5': headingCss(typography, colors, 'h5', isServerRendering),
        'heading.h6': headingCss(typography, colors, 'h6', isServerRendering),
        'body.small': bodyCss(typography, colors, 'small'),
        'body.medium': bodyCss(typography, colors, 'medium'),
        'body.large': bodyCss(typography, colors, 'large'),
        'link.link1': linkCss(typography, colors, 'link1'),
        'link.link2': linkCss(typography, colors, 'link2'),
      },
    },
    defaultVariants: {
      variant: 'body.medium',
    },
  });
};

export type TypographyVariantProps = VariantProps<ReturnType<typeof createTypographyVariants>>;
